/**
 * Cele 4 canale redundante ale unei stari: `[glif] [eticheta] [pastila] [bara de 3px]`.
 * In celula compacta: glif + bara + litera.
 *
 * CULOAREA NU E NICIODATA CODIFICAREA. Sase nuante de status nu pot fi facute separabile doar
 * prin nuanta (rosu<->verde si galben<->portocaliu sunt coliziunile clasice la deficiente de
 * vedere a culorilor), deci glifului nu e decorativ — e obligatoriu. Silueta fiecarui glif e
 * diferita (cerc / octogon / bare / ceas / romb / triunghi) ca sa se distinga si la 12px.
 *
 * Toate glifurile mostenesc `currentColor` si nu au `id`-uri interne (fara `<pattern>`), ca sa
 * poata fi randate de N ori pe pagina fara coliziuni de `id`.
 */

import type { JSX } from 'react';
import { definitieStare, type CodStare } from '../lib/stari.ts';

interface PropsGlif {
  /** Dimensiunea in px; implicit ia `--pill-glyph`. */
  size?: number | string;
  className?: string;
}

const comune = {
  viewBox: '0 0 16 16',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
  focusable: false,
} satisfies JSX.IntrinsicElements['svg'];

function svgProps({ size, className }: PropsGlif) {
  return {
    ...comune,
    width: size ?? 'var(--pill-glyph)',
    height: size ?? 'var(--pill-glyph)',
    className,
  };
}

/** Aprobat — bifa in cerc. */
export function GlifAprobat(props: PropsGlif) {
  return (
    <svg {...svgProps(props)}>
      <circle cx="8" cy="8" r="6.4" />
      <path d="M5 8.3 7.1 10.4 11.1 5.9" />
    </svg>
  );
}

/** Blocat — interdictie in octogon (silueta de „stop", nu cerc). */
export function GlifBlocat(props: PropsGlif) {
  return (
    <svg {...svgProps(props)}>
      <path d="M5.6 1.7h4.8l3.9 3.9v4.8l-3.9 3.9H5.6l-3.9-3.9V5.6z" />
      <path d="M4.9 11.1 11.1 4.9" />
    </svg>
  );
}

/** Esalonat — bare stivuite tiate la 45 de grade (hasura o continua in fundalul celulei). */
export function GlifEsalonat(props: PropsGlif) {
  return (
    <svg {...svgProps(props)}>
      <path d="M2.4 4h11.2M2.4 8h11.2M2.4 12h11.2" />
      <path d="M12.6 3 4 12.4" strokeWidth="1" />
    </svg>
  );
}

/** In asteptare — ceas in cerc gol. */
export function GlifAsteptare(props: PropsGlif) {
  return (
    <svg {...svgProps(props)}>
      <circle cx="8" cy="8" r="6.4" />
      <path d="M8 4.4V8l2.6 1.7" />
    </svg>
  );
}

/** De verificat — semn de intrebare in romb. */
export function GlifVerificat(props: PropsGlif) {
  return (
    <svg {...svgProps(props)}>
      <path d="M8 1.4 14.6 8 8 14.6 1.4 8z" />
      <path d="M6.6 6.3a1.5 1.5 0 1 1 1.9 1.7v.9" strokeWidth="1.4" />
      <path d="M8.4 11.2h.01" strokeWidth="1.8" />
    </svg>
  );
}

/** Urgent — semn de exclamare in triunghi. */
export function GlifUrgent(props: PropsGlif) {
  return (
    <svg {...svgProps(props)}>
      <path d="M8 1.9 14.8 13.9H1.2z" />
      <path d="M8 6.1v3.4" />
      <path d="M8 11.9h.01" strokeWidth="1.8" />
    </svg>
  );
}

/** Nesetat — cerc punctat. „Nicio decizie" e o stare, nu absenta unei stari. */
export function GlifNesetat(props: PropsGlif) {
  return (
    <svg {...svgProps(props)}>
      <circle cx="8" cy="8" r="6.4" strokeDasharray="2 2.2" />
    </svg>
  );
}

/**
 * Glif per stare. Partial pe scop: doar cele 6 stari din foaia `legenda` au silueta proprie.
 * Starile terminale (`paid`, `postponed`, `cancelled`) si `unset` cad pe glifui neutru — ele apar
 * in istoric si in liste, nu in ciclarea din grila.
 */
const GLIFURI: Partial<Record<CodStare, (props: PropsGlif) => JSX.Element>> = {
  approved: GlifAprobat,
  blocked: GlifBlocat,
  staged: GlifEsalonat,
  waiting: GlifAsteptare,
  verify: GlifVerificat,
  urgent: GlifUrgent,
};

export function GlifStare({ stare, ...rest }: PropsGlif & { stare: CodStare | null }) {
  const Glif = (stare === null ? undefined : GLIFURI[stare]) ?? GlifNesetat;
  return <Glif {...rest} />;
}

// ---------------------------------------------------------------------------------------------

export interface PropsStatusToken {
  stare: CodStare | null;
  /**
   * `complet` — bara + pastila cu glif si eticheta (liste, drawer, legenda).
   * `compact` — bara + glif + litera (celula de grila, unde nu incape eticheta).
   * `glif`    — doar glif + nume accesibil (matrice foarte densa).
   */
  varianta?: 'complet' | 'compact' | 'glif';
  /** Bara de 3px la stanga. Implicit pornita — e al patrulea canal. */
  cuBara?: boolean;
  className?: string;
}

export function StatusToken({
  stare,
  varianta = 'complet',
  cuBara = true,
  className,
}: PropsStatusToken) {
  const def = stare === null ? null : definitieStare(stare);
  const eticheta = def?.eticheta ?? 'Fără stare';
  const litera = def?.litera ?? '·';
  const esalonat = stare === 'staged';

  return (
    <span
      data-stare={stare ?? 'nesetat'}
      className={`inline-flex items-stretch gap-1.5 ${className ?? ''}`}
      title={def?.descriere ?? 'Stare nesetată'}
    >
      {cuBara ? <span className="stare-bara" aria-hidden="true" /> : null}

      {varianta === 'complet' ? (
        <span className={`pastila${esalonat ? ' hasura' : ''}`}>
          <GlifStare stare={stare} className="pastila-glif" />
          {eticheta}
        </span>
      ) : varianta === 'compact' ? (
        <span className="inline-flex items-center gap-1 text-11 leading-none text-stare-text">
          <GlifStare stare={stare} className="pastila-glif" />
          <span aria-hidden="true" className="num font-medium">
            {litera}
          </span>
          <span className="doar-sr">{eticheta}</span>
        </span>
      ) : (
        <span className="inline-flex items-center text-stare-text">
          <GlifStare stare={stare} className="pastila-glif" />
          <span className="doar-sr">{eticheta}</span>
        </span>
      )}
    </span>
  );
}
